"use client";

import { useEffect, useState } from "react";
import { Bot } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ApiError, api, loadTokens, type TestBotStatus } from "@/lib/api";

/**
 * EPIC-017 STORY-361 — "TEST BOT ACTIVE" banner.
 *
 * Site-wide indicator that satisfies the CLAUDE.md hard requirement:
 * the test bot harness must show a visible indicator while running.
 * Renders above the BetaBanner / SiteHeader on every page so the
 * admin always sees it regardless of where they navigate.
 *
 * Visibility rules:
 *   - Logged-out: never renders (no API call either)
 *   - Logged-in non-admin: never renders. The status endpoint is
 *     admin-gated; we check role first to avoid noisy 403s.
 *   - Admin: polls every 30 s, renders when active=true.
 *
 * The harness is disabled in production by TestBotGuard (NODE_ENV
 * + TEST_BOT_ENABLED), so even if a request slips through, the 403
 * is silently swallowed and the banner stays hidden.
 */

// Session-scoped sticky flag — when the harness 403s once (i.e. the
// TestBotGuard is currently OFF), we stop hitting the endpoint for
// the remainder of the session to silence browser-console noise on
// production where the harness is permanently disabled. Cleared on
// tab close (sessionStorage), so re-enabling the harness in dev
// just needs a tab reload.
const HARNESS_DISABLED_KEY = "test-bot.disabled-403";

function harnessKnownDisabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(HARNESS_DISABLED_KEY) === "1";
  } catch {
    return false;
  }
}

function rememberHarnessDisabled(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(HARNESS_DISABLED_KEY, "1");
  } catch {
    // sessionStorage can throw in some private-mode contexts; in
    // that case we just keep polling — noisier console but correct.
  }
}

export function TestBotBanner() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<TestBotStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    // Short-circuit if a previous poll in this tab already 403'd —
    // production has the harness permanently disabled and the banner
    // staying hidden is the only correct render, so there's no value
    // in continuing to hammer the endpoint and spam the console.
    if (harnessKnownDisabled()) return;

    async function pollIfAdmin() {
      if (!loadTokens()) return;
      try {
        const me = await api.me();
        if (cancelled || me.role !== "admin") return;
        const tick = async () => {
          try {
            const next = await api.getTestBotStatus();
            if (!cancelled) setStatus(next);
          } catch (err) {
            // 403 = harness disabled (production default). Cache it
            // in sessionStorage so subsequent renders / new tabs in
            // the same session don't re-poll. Banner stays hidden.
            if (err instanceof ApiError && err.status === 403) {
              rememberHarnessDisabled();
              if (pollTimer) {
                clearInterval(pollTimer);
                pollTimer = null;
              }
            }
            if (!cancelled) setStatus(null);
          }
        };
        await tick();
        // Only schedule recurring polls if the first tick didn't
        // already trigger the 403 short-circuit above.
        if (!cancelled && !harnessKnownDisabled()) {
          pollTimer = setInterval(tick, 30_000);
        }
      } catch {
        // me() failed (token expired etc.) — leave banner hidden
      }
    }

    void pollIfAdmin();
    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
    };
  }, []);

  if (!status?.active || !status.session) return null;

  // Absolute end-time avoids a Date.now() call in render (React 19
  // hooks-purity rule) and avoids a second interval just to drive a
  // countdown. Admin sees when the harness will auto-stop.
  const expiresAt = new Date(status.session.expiresAt);
  const hh = expiresAt.getHours().toString().padStart(2, "0");
  const mm = expiresAt.getMinutes().toString().padStart(2, "0");

  return (
    <div
      role="status"
      aria-live="polite"
      className="w-full border-b text-center text-[11px] font-bold uppercase tracking-[1px]"
      style={{
        borderColor: "color-mix(in srgb, var(--color-status-warning) 40%, transparent)",
        background: "color-mix(in srgb, var(--color-status-warning) 16%, transparent)",
        color: "var(--color-status-warning)",
      }}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-center gap-1.5 px-5 py-1.5">
        <Bot size={12} strokeWidth={2} aria-hidden="true" />
        {t("testBotBanner.active")} — &ldquo;{status.session.label}&rdquo; ·{" "}
        {t("testBotBanner.autoStopsAt", { time: `${hh}:${mm}` })}
      </div>
    </div>
  );
}
